/**
 * OAuth Integration for Consumer App
 *
 * Uses @ownyou/oauth package for unified OAuth management.
 * This module provides configured instances for Gmail and Outlook OAuth.
 *
 * Adapted from admin-dashboard for Vite environment.
 *
 * @see packages/oauth/src/index.ts
 */

import {
  GoogleOAuthProvider,
  MicrosoftOAuthProvider,
  BrowserTokenStorage,
  TokenManager,
  GOOGLE_DEFAULT_SCOPES,
  MICROSOFT_DEFAULT_SCOPES,
  type StoredTokens,
} from '@ownyou/oauth'
import { getPublicRuntimeConfigSnapshot, loadPublicRuntimeConfig } from '../config/runtime-public-config'

// Default user ID for consumer app
const DEFAULT_USER_ID = 'default_user'
const OAUTH_WORKER_CALLBACK_URL = 'https://ownyou-oauth-callback.nlongcroft.workers.dev/callback'
const OAUTH_WORKER_REFRESH_URL = 'https://ownyou-oauth-callback.nlongcroft.workers.dev/refresh'

type GoogleSourceId = 'gmail' | 'google-calendar'

const GOOGLE_SOURCE_SCOPES: Record<GoogleSourceId, string[]> = {
  gmail: ['https://www.googleapis.com/auth/gmail.readonly'],
  'google-calendar': ['https://www.googleapis.com/auth/calendar.readonly'],
}

function getGoogleClientId(): string {
  return import.meta.env.VITE_GOOGLE_CLIENT_ID || getPublicRuntimeConfigSnapshot().googleClientId || ''
}

function getMicrosoftClientId(): string {
  return import.meta.env.VITE_MICROSOFT_CLIENT_ID || getPublicRuntimeConfigSnapshot().microsoftClientId || ''
}

async function ensureGoogleClientId(): Promise<string> {
  let clientId = getGoogleClientId()
  if (clientId) return clientId
  await loadPublicRuntimeConfig()
  clientId = getGoogleClientId()
  if (!clientId) throw new Error('Google OAuth client ID is not configured')
  return clientId
}

async function ensureMicrosoftClientId(): Promise<string> {
  let clientId = getMicrosoftClientId()
  if (clientId) return clientId
  await loadPublicRuntimeConfig()
  clientId = getMicrosoftClientId()
  if (!clientId) throw new Error('Microsoft OAuth client ID is not configured')
  return clientId
}

function createOAuthState(sourceId: GoogleSourceId): string {
  const nonce = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('')
  const state = `${sourceId}:${nonce}`
  localStorage.setItem(`oauth_worker_state_${sourceId}`, state)
  return state
}

export function buildGoogleWorkerOAuthUrl(sourceId: GoogleSourceId = 'gmail'): string | null {
  const clientId = getGoogleClientId()
  if (!clientId) return null

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: OAUTH_WORKER_CALLBACK_URL,
    scope: GOOGLE_SOURCE_SCOPES[sourceId].join(' '),
    state: createOAuthState(sourceId),
    access_type: 'offline',
    prompt: 'consent',
  })

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
}

interface WorkerOAuthResult {
  accessToken: string
  refreshToken?: string
  expiresAt?: number
  provider: 'google' | 'microsoft'
}

// Create singleton storage instance
let _storage: BrowserTokenStorage | null = null

function getStorage(): BrowserTokenStorage {
  if (!_storage) {
    _storage = new BrowserTokenStorage()
  }
  return _storage
}

// Create singleton token manager
let _tokenManager: TokenManager | null = null

export function getTokenManager(): TokenManager {
  if (!_tokenManager) {
    _tokenManager = new TokenManager(getStorage())
  }
  return _tokenManager
}

/**
 * Get Google OAuth provider configured for consumer app
 */
export function getGoogleProvider(): GoogleOAuthProvider {
  const clientId = getGoogleClientId()

  if (!clientId) {
    throw new Error('Google OAuth client ID is not configured')
  }

  // Browser builds must not depend on a bundled Google client secret. The
  // current PWA authorization path exchanges codes through the Cloudflare OAuth
  // worker. This provider remains for revocation and legacy fallback paths.
  return new GoogleOAuthProvider({
    provider: 'google',
    platform: 'browser',
    clientId,
    redirectUri: `${typeof window !== 'undefined' ? window.location.origin : ''}/oauth/gmail/callback`,
    scopes: GOOGLE_DEFAULT_SCOPES,
  })
}

/**
 * Get Microsoft OAuth provider configured for consumer app
 */
export function getMicrosoftProvider(): MicrosoftOAuthProvider {
  const clientId = getMicrosoftClientId()

  if (!clientId) {
    throw new Error('Microsoft OAuth client ID is not configured')
  }

  return new MicrosoftOAuthProvider({
    provider: 'microsoft',
    platform: 'browser',
    clientId,
    redirectUri: `${typeof window !== 'undefined' ? window.location.origin : ''}/oauth/outlook/callback`,
    scopes: MICROSOFT_DEFAULT_SCOPES,
    tenantId: 'consumers', // Personal Microsoft accounts
  })
}

/**
 * Start Google OAuth flow
 * @param returnUrl - URL to redirect back to after OAuth completes (default: current page)
 */
async function openWorkerOAuthPopup(
  oauthUrl: string,
  sourceId: GoogleSourceId
): Promise<WorkerOAuthResult | null> {
  return new Promise((resolve) => {
    const width = 500
    const height = 650
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const popup = window.open(
      oauthUrl,
      `oauth_${sourceId}`,
      `width=${width},height=${height},left=${left},top=${top},popup=yes`
    )

    if (!popup) {
      resolve(null)
      return
    }
    const workerPopup = popup

    function cleanup() {
      clearInterval(pollTimer)
      clearTimeout(timeout)
      window.removeEventListener('message', handleMessage)
    }

    function handleMessage(event: MessageEvent) {
      if (event.origin !== 'https://ownyou-oauth-callback.nlongcroft.workers.dev') return
      if (event.data?.type !== 'oauth_callback' || event.data?.sourceId !== sourceId) return

      const expectedState = localStorage.getItem(`oauth_worker_state_${sourceId}`)
      localStorage.removeItem(`oauth_worker_state_${sourceId}`)

      cleanup()
      workerPopup.close()

      if (expectedState && event.data.state !== expectedState) {
        console.error('[OAuth] Worker OAuth state mismatch')
        resolve(null)
        return
      }

      if (!event.data.success || !event.data.accessToken) {
        console.error('[OAuth] Worker OAuth failed:', event.data.error || 'unknown error')
        resolve(null)
        return
      }

      resolve({
        accessToken: event.data.accessToken,
        refreshToken: event.data.refreshToken,
        expiresAt: event.data.expiresAt,
        provider: event.data.provider ?? 'google',
      })
    }

    window.addEventListener('message', handleMessage)

    const pollTimer = setInterval(() => {
      if (workerPopup.closed) {
        cleanup()
        resolve(null)
      }
    }, 500)

    const timeout = setTimeout(() => {
      if (!workerPopup.closed) workerPopup.close()
      cleanup()
      resolve(null)
    }, 5 * 60 * 1000)
  })
}

export async function startGoogleAuth(
  returnUrl?: string,
  sourceId: GoogleSourceId = 'gmail'
): Promise<StoredTokens | null> {
  await ensureGoogleClientId()
  const authUrl = buildGoogleWorkerOAuthUrl(sourceId)
  if (!authUrl) {
    throw new Error('Google OAuth client ID is not configured')
  }

  // Store return URL for redirect after OAuth completes
  const redirectTo = returnUrl || window.location.pathname
  localStorage.setItem('oauth_return_url', redirectTo)

  console.log('[OAuth] Starting Google auth flow:', {
    origin: window.location.origin,
    redirectUri: OAUTH_WORKER_CALLBACK_URL,
    returnUrl: redirectTo,
    authUrl: authUrl.substring(0, 100) + '...',
  })

  const result = await openWorkerOAuthPopup(authUrl, sourceId)
  if (!result) return null

  const tokens: StoredTokens = {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken ?? '',
    expiresAt: result.expiresAt ?? Date.now() + 3600000,
    scope: GOOGLE_SOURCE_SCOPES[sourceId].join(' '),
    tokenType: 'Bearer',
  }

  const tokenManager = getTokenManager()
  await tokenManager.storeTokens(DEFAULT_USER_ID, 'google', tokens)

  // Preserve the existing redirect handoff for routes that pick tokens up from
  // Settings/DataSourceContext after OAuth completes.
  sessionStorage.setItem('oauth_token_data', JSON.stringify({
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: tokens.expiresAt,
    provider: 'google',
  }))
  sessionStorage.setItem('oauth_provider', sourceId)

  return tokens
}

/**
 * Start Microsoft OAuth flow with PKCE
 * PKCE (Proof Key for Code Exchange) is required by Microsoft for browser SPAs
 * @param returnUrl - URL to redirect back to after OAuth completes (default: current page)
 */
export async function startMicrosoftAuth(returnUrl?: string): Promise<void> {
  await ensureMicrosoftClientId()
  const provider = getMicrosoftProvider()
  const state = crypto.randomUUID()

  // Store state for CSRF validation in callback
  sessionStorage.setItem('oauth_state_microsoft', state)

  // Store return URL for redirect after OAuth completes
  const redirectTo = returnUrl || window.location.pathname
  localStorage.setItem('oauth_return_url', redirectTo)

  // Get authorization URL with PKCE code verifier
  const { url, codeVerifier } = await provider.getAuthorizationUrl(state)

  // Store code verifier for use in callback (required for PKCE token exchange)
  sessionStorage.setItem('oauth_pkce_verifier_microsoft', codeVerifier)

  window.location.href = url
}

/**
 * Handle Google OAuth callback - exchange code for tokens
 */
export async function handleGoogleCallback(
  code: string,
  state: string
): Promise<StoredTokens> {
  // Validate state for CSRF protection
  const storedState = sessionStorage.getItem('oauth_state_google')
  sessionStorage.removeItem('oauth_state_google')

  if (state !== storedState) {
    throw new Error('Invalid OAuth state - possible CSRF attack')
  }

  const provider = getGoogleProvider()
  const tokens = await provider.exchangeCode(code)

  // Store tokens using TokenManager
  const tokenManager = getTokenManager()
  await tokenManager.storeTokens(DEFAULT_USER_ID, 'google', tokens)

  return tokens
}

/**
 * Handle Microsoft OAuth callback - exchange code for tokens with PKCE
 */
export async function handleMicrosoftCallback(
  code: string,
  state: string
): Promise<StoredTokens> {
  // Retrieve and clean up OAuth state
  const storedState = sessionStorage.getItem('oauth_state_microsoft')
  const codeVerifier = sessionStorage.getItem('oauth_pkce_verifier_microsoft')

  sessionStorage.removeItem('oauth_state_microsoft')
  sessionStorage.removeItem('oauth_pkce_verifier_microsoft')

  // Validate state for CSRF protection
  if (state !== storedState) {
    throw new Error('Invalid OAuth state - possible CSRF attack')
  }

  // Validate PKCE code verifier exists
  if (!codeVerifier) {
    throw new Error('Missing PKCE code verifier - OAuth flow may have been interrupted')
  }

  const provider = getMicrosoftProvider()
  // Pass codeVerifier for PKCE token exchange
  const tokens = await provider.exchangeCode(code, codeVerifier)

  // Store tokens using TokenManager
  const tokenManager = getTokenManager()
  await tokenManager.storeTokens(DEFAULT_USER_ID, 'microsoft', tokens)

  return tokens
}

/**
 * Check if Google is connected (has valid tokens or can refresh)
 *
 * Returns true if:
 * 1. Access token is valid (not expired), OR
 * 2. We have a refresh token (can get new access token)
 *
 * This prevents unnecessary re-authentication when access token expired
 * but refresh token is still valid.
 */
export async function isGoogleConnected(): Promise<boolean> {
  const tokenManager = getTokenManager()
  const isValid = await tokenManager.isTokenValid(DEFAULT_USER_ID, 'google')
  const tokens = await tokenManager.getTokens(DEFAULT_USER_ID, 'google')
  const hasRefreshToken = !!tokens?.refreshToken

  console.log('[OAuth] isGoogleConnected check:', {
    hasTokens: !!tokens,
    isValid,
    expiresAt: tokens?.expiresAt ? new Date(tokens.expiresAt).toISOString() : null,
    hasRefreshToken,
  })

  // Connected if access token valid OR we can refresh
  return isValid || hasRefreshToken
}

/**
 * Check if Microsoft is connected (has valid tokens or can refresh)
 *
 * Returns true if:
 * 1. Access token is valid (not expired), OR
 * 2. We have a refresh token (can get new access token)
 *
 * This prevents unnecessary re-authentication when access token expired
 * but refresh token is still valid.
 */
export async function isMicrosoftConnected(): Promise<boolean> {
  const tokenManager = getTokenManager()
  const isValid = await tokenManager.isTokenValid(DEFAULT_USER_ID, 'microsoft')
  const tokens = await tokenManager.getTokens(DEFAULT_USER_ID, 'microsoft')
  const hasRefreshToken = !!tokens?.refreshToken

  console.log('[OAuth] isMicrosoftConnected check:', {
    hasTokens: !!tokens,
    isValid,
    expiresAt: tokens?.expiresAt ? new Date(tokens.expiresAt).toISOString() : null,
    hasRefreshToken,
  })

  // Connected if access token valid OR we can refresh
  return isValid || hasRefreshToken
}

/**
 * Error class for when re-authentication is required
 * (e.g., refresh token expired or revoked)
 */
export class ReAuthRequiredError extends Error {
  constructor(provider: 'google' | 'microsoft', reason: string) {
    super(`Re-authentication required for ${provider}: ${reason}`)
    this.name = 'ReAuthRequiredError'
  }
}

async function refreshViaOAuthWorker(
  refreshToken: string,
  provider: 'google' | 'microsoft'
): Promise<StoredTokens | null> {
  try {
    const url = new URL(OAUTH_WORKER_REFRESH_URL)
    url.searchParams.set('refresh_token', refreshToken)
    url.searchParams.set('provider', provider)

    const response = await fetch(url.toString())
    const data = await response.json().catch(() => ({}))

    if (!response.ok || data.error) {
      console.warn('[OAuth] Worker token refresh failed:', data.error || response.status)
      return null
    }

    return {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken || refreshToken,
      expiresAt: typeof data.expiresIn === 'number'
        ? Date.now() + data.expiresIn * 1000
        : Date.now() + 3600000,
      scope: typeof data.scope === 'string' ? data.scope : '',
      tokenType: 'Bearer',
    }
  } catch (err) {
    console.warn('[OAuth] Worker token refresh error:', err)
    return null
  }
}

/**
 * Get valid Google access token (auto-refreshes if needed)
 *
 * @throws {ReAuthRequiredError} When refresh token has expired and user needs to re-authenticate
 */
export async function getGoogleAccessToken(): Promise<string> {
  const tokenManager = getTokenManager()
  const tokens = await tokenManager.getTokens(DEFAULT_USER_ID, 'google')

  if (!tokens) {
    throw new ReAuthRequiredError('google', 'No tokens available')
  }

  // Check if refresh is needed
  if (await tokenManager.needsRefresh(DEFAULT_USER_ID, 'google')) {
    try {
      const newTokens = await refreshViaOAuthWorker(tokens.refreshToken, 'google')
        ?? await getGoogleProvider().refreshTokens(tokens.refreshToken)
      await tokenManager.storeTokens(DEFAULT_USER_ID, 'google', newTokens)
      return newTokens.accessToken
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err)

      // Check for invalid_grant error (refresh token expired/revoked)
      if (errorMessage.includes('invalid_grant')) {
        console.log('[OAuth] Google refresh token expired, clearing tokens and requiring re-auth')
        // Clear the invalid tokens
        await tokenManager.deleteTokens(DEFAULT_USER_ID, 'google')
        throw new ReAuthRequiredError('google', 'Refresh token expired')
      }

      // Re-throw other errors as-is
      throw err
    }
  }

  return tokens.accessToken
}

/**
 * Get valid Microsoft access token (auto-refreshes if needed)
 *
 * @throws {ReAuthRequiredError} When refresh token has expired and user needs to re-authenticate
 */
export async function getMicrosoftAccessToken(): Promise<string> {
  const tokenManager = getTokenManager()
  const tokens = await tokenManager.getTokens(DEFAULT_USER_ID, 'microsoft')

  if (!tokens) {
    throw new ReAuthRequiredError('microsoft', 'No tokens available')
  }

  // Check if refresh is needed
  if (await tokenManager.needsRefresh(DEFAULT_USER_ID, 'microsoft')) {
    const provider = getMicrosoftProvider()
    try {
      const newTokens = await provider.refreshTokens(tokens.refreshToken)
      await tokenManager.storeTokens(DEFAULT_USER_ID, 'microsoft', newTokens)
      return newTokens.accessToken
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err)

      // Check for invalid_grant error (refresh token expired/revoked)
      if (errorMessage.includes('invalid_grant')) {
        console.log('[OAuth] Microsoft refresh token expired, clearing tokens and requiring re-auth')
        // Clear the invalid tokens
        await tokenManager.deleteTokens(DEFAULT_USER_ID, 'microsoft')
        throw new ReAuthRequiredError('microsoft', 'Refresh token expired')
      }

      // Re-throw other errors as-is
      throw err
    }
  }

  return tokens.accessToken
}

/**
 * Disconnect Google (revoke and delete tokens)
 */
export async function disconnectGoogle(): Promise<void> {
  const tokenManager = getTokenManager()
  const tokens = await tokenManager.getTokens(DEFAULT_USER_ID, 'google')

  if (tokens) {
    try {
      const provider = getGoogleProvider()
      await provider.revokeTokens(tokens)
    } catch (err) {
      console.warn('[OAuth] Failed to revoke Google tokens:', err)
    }
  }

  await tokenManager.deleteTokens(DEFAULT_USER_ID, 'google')
}

/**
 * Get the URL to redirect to after OAuth completes
 * Returns the stored return URL or falls back to /ab-testing
 */
export function getOAuthReturnUrl(): string {
  const returnUrl = localStorage.getItem('oauth_return_url')
  // Clean up after reading
  localStorage.removeItem('oauth_return_url')
  return returnUrl || '/connections'
}

/**
 * Pending OAuth action with config
 */
export interface PendingOAuthAction {
  action: string
  provider?: string
  maxEmails?: number
}

/**
 * Store a pending action to be executed after OAuth completes
 * This allows the OAuth flow to automatically continue after redirect
 */
export function setPendingOAuthAction(action: string, config?: { provider?: string; maxEmails?: number }): void {
  const pendingAction: PendingOAuthAction = { action, ...config }
  localStorage.setItem('oauth_pending_action', JSON.stringify(pendingAction))
}

/**
 * Get and clear the pending OAuth action
 * Returns null if no action is pending
 */
export function getPendingOAuthAction(): PendingOAuthAction | null {
  const actionStr = localStorage.getItem('oauth_pending_action')
  localStorage.removeItem('oauth_pending_action')
  if (!actionStr) return null
  try {
    return JSON.parse(actionStr) as PendingOAuthAction
  } catch {
    // Legacy format - just action string
    return { action: actionStr }
  }
}

/**
 * Check if there's a pending OAuth action without clearing it
 * Use this to check before overwriting state from other sources
 */
export function hasPendingOAuthAction(): boolean {
  return localStorage.getItem('oauth_pending_action') !== null
}

/**
 * Clear the pending OAuth action without returning it
 */
export function clearPendingOAuthAction(): void {
  localStorage.removeItem('oauth_pending_action')
}

/**
 * Disconnect Microsoft (revoke and delete tokens)
 */
export async function disconnectMicrosoft(): Promise<void> {
  const tokenManager = getTokenManager()
  const tokens = await tokenManager.getTokens(DEFAULT_USER_ID, 'microsoft')

  if (tokens) {
    try {
      const provider = getMicrosoftProvider()
      await provider.revokeTokens(tokens)
    } catch (err) {
      console.warn('[OAuth] Failed to revoke Microsoft tokens:', err)
    }
  }

  await tokenManager.deleteTokens(DEFAULT_USER_ID, 'microsoft')
}

/**
 * Clear stale OAuth state that may have been left behind from failed OAuth flows.
 * This checks if there's a pending action but no valid tokens, which indicates
 * a previous OAuth attempt failed after redirect but before token storage.
 *
 * Call this on page load to auto-recover from broken OAuth states.
 */
export async function clearStaleOAuthState(): Promise<{ wasStale: boolean; reason?: string }> {
  // Check if there's a pending action
  const hasPending = hasPendingOAuthAction()
  if (!hasPending) {
    return { wasStale: false }
  }

  // Get the pending action to check what provider it was for
  const pendingStr = localStorage.getItem('oauth_pending_action')
  if (!pendingStr) {
    return { wasStale: false }
  }

  let pending: PendingOAuthAction
  try {
    pending = JSON.parse(pendingStr)
  } catch {
    // Invalid JSON - definitely stale
    console.warn('[OAuth] Found invalid pending action JSON, clearing stale state')
    clearPendingOAuthAction()
    localStorage.removeItem('oauth_return_url')
    return { wasStale: true, reason: 'invalid_json' }
  }

  // Determine which providers need valid tokens based on the pending action
  const needsGoogle = pending.provider === 'gmail' || pending.provider === 'both'
  const needsMicrosoft = pending.provider === 'outlook' || pending.provider === 'both'

  // Check if the required providers have valid tokens
  const [googleValid, microsoftValid] = await Promise.all([
    needsGoogle ? isGoogleConnected() : Promise.resolve(true),
    needsMicrosoft ? isMicrosoftConnected() : Promise.resolve(true),
  ])

  // If tokens exist for all required providers, the pending action is valid
  if (googleValid && microsoftValid) {
    console.log('[OAuth] Pending action has valid tokens, keeping state')
    return { wasStale: false }
  }

  // For 'both' provider: if at least one is valid, we're in a sequential OAuth flow
  // The first OAuth completed, now waiting to auth the second provider
  // Don't clear the pending action - we're in progress, not stale
  if (pending.provider === 'both' && (googleValid || microsoftValid)) {
    console.log('[OAuth] Pending action is in progress (sequential OAuth flow), keeping state')
    return { wasStale: false }
  }

  // Stale state detected - tokens don't exist for required providers
  const missingProviders = []
  if (needsGoogle && !googleValid) missingProviders.push('google')
  if (needsMicrosoft && !microsoftValid) missingProviders.push('microsoft')

  console.warn('[OAuth] Detected stale OAuth state - pending action exists but tokens missing for:', missingProviders)
  console.warn('[OAuth] This can happen if a previous OAuth flow failed. Clearing stale state.')

  // Clear the stale state
  clearPendingOAuthAction()
  localStorage.removeItem('oauth_return_url')

  return { wasStale: true, reason: `missing_tokens:${missingProviders.join(',')}` }
}
